/**
 * Application routing configuration
 * @author Maheen Nasir
 * @description Defines the application routes with lazy loading and suspense handling
 */

import { Suspense } from "react";
import { Navigate, type RouteObject } from "react-router-dom";
import type { ReactElement, ComponentType } from "react";
import Spinner from "@components/common/Spinner";
import { BROWSER_ROUTES } from "@constants/browser.routes";
import AuthGuard from "@components/RouteGuards/AuthGuard";
import GuestGuard from "@components/RouteGuards/GuestGuard";
import ResetRouteGaurd from "@components/RouteGuards/ResetGuard";
import { MainLayout } from "@components/Layout/MainLayout";
import { lazyWithRetry } from "@utils/lazyWithRetry";

// Lazy load components
const NotFound = lazyWithRetry(() => import("@pages/404"));
const Unauthorized = lazyWithRetry(() => import("@pages/Unauthorized"));
const InternalServerError = lazyWithRetry(() => import("@pages/500"));
const Login = lazyWithRetry(() => import("@pages/Login"));
const Signup = lazyWithRetry(() => import("@pages/Signup"));
const ForgetPassword = lazyWithRetry(() => import("@pages/ForgetPassword"));
const RegistrationRecovery = lazyWithRetry(() => import("@pages/RegistrationRecovery"));
const VerifyOtp = lazyWithRetry(() => import("@pages/VerifyOtp"));
const ResetPassword = lazyWithRetry(() => import("@pages/ResetPassword"));
const CreatePassword = lazyWithRetry(() => import("@features/Signup/components/CreatePassword"));
const AdmissionForm = lazyWithRetry(() => import("@features/Signup/Admission/AdmissionForm"));
const PrivacyPolicy = lazyWithRetry(() => import("@pages/PrivacyPolicy"));
const TermsAndConditions = lazyWithRetry(() => import("@pages/TermsAndConditions"));
const FAQ = lazyWithRetry(() => import("@pages/FAQ"));
const Dashboard = lazyWithRetry(() => import("@components/Dashboard/Dashboard"));
const TrackApplicationPage = lazyWithRetry(() => import("@pages/TrackApplication"));
const MyCoursesPage = lazyWithRetry(() => import("@pages/MyCourses"));
const MyResultsPage = lazyWithRetry(() => import("@pages/MyResults"));
const JobPage = lazyWithRetry(() => import("@pages/Job"));
const JobsPage = lazyWithRetry(() => import("@pages/Jobs"));
const JobRequestsPage = lazyWithRetry(() => import("@pages/JobRequests"));
const ShortlistedPage = lazyWithRetry(() => import("@pages/Shortlisted"));
const SelectedPage = lazyWithRetry(() => import("@pages/Selected"));
const ApplyNow = lazyWithRetry(() => import("@components/Dashboard/components/ApplyNow"));
const CourseDetail = lazyWithRetry(() => import("@features/MyCourses/components/CourseDetail/CourseDetail"));

/**
 * Higher-order component to wrap lazy-loaded components with Suspense
 * @param {React.LazyExoticComponent<ComponentType>} Component - The lazy-loaded component to wrap
 * @returns {JSX.Element} The wrapped component with Suspense
 */
const withSuspense = (Component: React.LazyExoticComponent<ComponentType>): ReactElement => {
	return (
		<Suspense fallback={<Spinner overlay />}>
			<Component />
		</Suspense>
	);
};

/**
 * Application route configuration
 * @description Defines the main routes of the application with their corresponding components
 * Now using 2-step admission form: Basic Info and Qualification
 */
export const routes: RouteObject[] = [
	{
		path: "/",
		element: (
			<AuthGuard>
				<MainLayout />
			</AuthGuard>
		),
		children: [
			{ index: true, element: withSuspense(Dashboard) },
			{ path: "track-application", element: withSuspense(TrackApplicationPage) },
			{ path: "my-courses", element: withSuspense(MyCoursesPage) },
			{ path: "my-results", element: withSuspense(MyResultsPage) },
			{ path: "my-courses/:uuid", element: withSuspense(CourseDetail) },
			{ path: "job", element: withSuspense(JobPage) },
			{ path: "jobs", element: withSuspense(JobsPage) },
			{ path: "job-requests", element: withSuspense(JobRequestsPage) },
			{ path: "shortlisted", element: withSuspense(ShortlistedPage) },
			{ path: "selected", element: withSuspense(SelectedPage) },
			{ path: "course/:id", element: withSuspense(ApplyNow) },
			// Profile/Admission routes - 2-step structure
			{ path: "profile", element: withSuspense(AdmissionForm) },
			{ path: "profile/basic-info", element: withSuspense(AdmissionForm) },
			{ path: "profile/qualification", element: withSuspense(AdmissionForm) },
			// Legacy routes - redirect to basic-info for backward compatibility
			{ path: "profile/personal-info", element: withSuspense(AdmissionForm) },
			{ path: "profile/guardian-info", element: withSuspense(AdmissionForm) },
			{ path: "profile/contact-info", element: withSuspense(AdmissionForm) },
			{ path: "profile/address-info", element: withSuspense(AdmissionForm) },
		],
	},
	{
		path: BROWSER_ROUTES.LOGIN,
		element: <GuestGuard>{withSuspense(Login)}</GuestGuard>,
	},
	{
		path: BROWSER_ROUTES.SIGNUP,
		element: <GuestGuard>{withSuspense(Signup)}</GuestGuard>,
	},
	{
		path: "/sign-up",
		element: <Navigate to={BROWSER_ROUTES.SIGNUP} replace />,
	},
	{
		path: "/sign-up/verify-otp",
		element: <Navigate to={BROWSER_ROUTES.SIGNUP_VERIFY_OTP} replace />,
	},
	{
		path: "/sign-up/reset-password",
		element: <Navigate to={BROWSER_ROUTES.SIGNUP_RESET_PASSWORD} replace />,
	},

	{
		path: BROWSER_ROUTES.FORGET_PASSWORD,
		element: <GuestGuard>{withSuspense(ForgetPassword)}</GuestGuard>,
	},
	{
		path: BROWSER_ROUTES.REGISTRATION_RECOVERY,
		element: <GuestGuard>{withSuspense(RegistrationRecovery)}</GuestGuard>,
	},
	{
		path: BROWSER_ROUTES.VERIFY_OTP,
		element: <GuestGuard>{withSuspense(VerifyOtp)}</GuestGuard>,
	},
	{
		path: BROWSER_ROUTES.SIGNUP_RESET_PASSWORD,
		element: <GuestGuard>{withSuspense(CreatePassword)}</GuestGuard>,
	},
	{
		path: BROWSER_ROUTES.RESET_PASSWORD,
		element: (
			<GuestGuard>
				<ResetRouteGaurd>{withSuspense(ResetPassword)}</ResetRouteGaurd>
			</GuestGuard>
		),
	},
	{
		path: BROWSER_ROUTES.UNAUTHORIZED,
		element: withSuspense(Unauthorized),
	},
	{
		path: BROWSER_ROUTES.SERVER_ERROR,
		element: withSuspense(InternalServerError),
	},
	{
		path: BROWSER_ROUTES.PRIVACY_POLICY,
		element: withSuspense(PrivacyPolicy),
	},
	{
		path: BROWSER_ROUTES.TERMS_AND_CONDITIONS,
		element: withSuspense(TermsAndConditions),
	},
	{
		path: BROWSER_ROUTES.FAQ,
		element: withSuspense(FAQ),
	},
	{
		path: "*",
		element: withSuspense(NotFound),
	},
];

export default routes;
